import { useSyncExternalStore } from 'react';

type InstallOutcome = 'accepted' | 'dismissed';
export type InstallResult = InstallOutcome | 'unavailable' | 'failed' | 'unconfirmed';
interface InstallEvent extends Event {
  prompt: () => Promise<{ outcome: InstallOutcome } | undefined> | void;
  userChoice?: Promise<{ outcome: InstallOutcome }>;
}
interface InstallState { available: boolean; busy: boolean; installed: boolean }
const SEEN_KEY = 'lianke:install-guide:seen:v1';
let seenInMemory = false;
let started = false;
let pending: InstallEvent | null = null;
let activeRequest: Promise<InstallResult> | null = null;
let state: InstallState = { available: false, busy: false, installed: false };
const listeners = new Set<() => void>();
function publish(patch: Partial<InstallState>) {
  state = { ...state, ...patch };
  listeners.forEach(listener => listener());
}
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}
export function hasSeenInstallGuide() {
  if (seenInMemory) return true;
  try { return localStorage.getItem(SEEN_KEY) === '1'; } catch { return false; }
}
export function rememberInstallGuide() {
  seenInMemory = true;
  try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* Still suppress for this page session. */ }
}
// Start before React/account loading: browsers may emit the install event only once.
export function initializeInstallPrompt() {
  if (started) return;
  started = true;
  publish({ installed: isStandalone() });
  window.addEventListener('beforeinstallprompt', event => {
    if (typeof (event as Partial<InstallEvent>).prompt !== 'function') return;
    event.preventDefault();
    pending = event as InstallEvent;
    publish({ available: !state.installed });
  });
  window.addEventListener('appinstalled', () => {
    pending = null;
    rememberInstallGuide();
    publish({ available: false, busy: false, installed: true });
  });
  const display = window.matchMedia('(display-mode: standalone), (display-mode: fullscreen), (display-mode: minimal-ui)');
  const updateDisplay = () => {
    if (isStandalone()) { pending = null; rememberInstallGuide(); publish({ installed: true, available: false }); }
  };
  if (typeof display.addEventListener === 'function') display.addEventListener('change', updateDisplay);
  else if (typeof display.addListener === 'function') display.addListener(updateDisplay);
}
const subscribe = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export const useInstallState = () => useSyncExternalStore(subscribe, () => state);
function readChoice(choice: unknown): Promise<InstallOutcome> {
  return Promise.resolve(choice).then(value => {
    const outcome = (value as { outcome?: unknown } | undefined)?.outcome;
    if (outcome === 'accepted' || outcome === 'dismissed') return outcome;
    // Some browsers return void from prompt(). Only a real choice confirms a result.
    return new Promise<never>(() => {});
  });
}
export function requestInstall(): Promise<InstallResult> {
  if (activeRequest) return activeRequest;
  if (!pending || state.installed) return Promise.resolve('unavailable');
  const event = pending;
  pending = null; // A prompt event can only be used once, including after cancellation.
  publish({ available: false, busy: true });
  const request = (async (): Promise<InstallResult> => {
    let timer: number | undefined;
    try {
      // Invoke the real prompt now, in the click. A silent browser must not lock the UI.
      const prompted = event.prompt();
      // Observe both channels immediately: a pending prompt() must not hide userChoice.
      const result = Promise.race([readChoice(prompted), readChoice(event.userChoice)]);
      const waitingLimit = new Promise<'unconfirmed'>(resolve => {
        timer = window.setTimeout(() => resolve('unconfirmed'), 12000);
      });
      const outcome = await Promise.race([result, waitingLimit]);
      rememberInstallGuide();
      return outcome;
    } catch {
      return 'failed';
    } finally {
      window.clearTimeout(timer);
      publish({ busy: false });
    }
  })();
  activeRequest = request;
  void request.then(() => { if (activeRequest === request) activeRequest = null; });
  return request;
}
